import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { AUTH_MESSAGE_KEYS, type AuthMessageKey } from "./schema";

// ./schema.ts 는 서버 워커 번들에도 들어가므로 lingui 를 못 쓴다. 사람이 읽는 문구는
// 여기 msg 디스크립터로 두고 화면에서 i18n._() 로 해석한다(언어 전환에 반응하도록
// 모듈 로드 시점에 문자열로 굳히지 않는다 — shared/features/reminder/labels.ts 와 동일).
// 이 파일을 import 하는 쪽은 클라이언트(@app/ui, withtask)뿐이어야 한다.

const MESSAGES: Record<AuthMessageKey, MessageDescriptor> = {
  [AUTH_MESSAGE_KEYS.emailRequired]: msg`이메일을 입력해 주세요.`,
  [AUTH_MESSAGE_KEYS.emailInvalid]: msg`올바른 이메일 주소를 입력해 주세요.`,
  [AUTH_MESSAGE_KEYS.emailDisposable]: msg`일회용/임시 이메일 주소로는 가입할 수 없어요. 다른 이메일을 사용해 주세요.`,
  [AUTH_MESSAGE_KEYS.nicknameRequired]: msg`닉네임을 입력해 주세요.`,
  [AUTH_MESSAGE_KEYS.passwordRequired]: msg`비밀번호를 입력해 주세요.`,
  [AUTH_MESSAGE_KEYS.passwordTooShort]: msg`비밀번호는 8자 이상이어야 합니다.`,
  [AUTH_MESSAGE_KEYS.passwordConfirmMismatch]: msg`비밀번호가 일치하지 않습니다.`,
  [AUTH_MESSAGE_KEYS.otpRequired]: msg`인증 코드를 입력해 주세요.`,
  [AUTH_MESSAGE_KEYS.otpLength]: msg`인증 코드 6자리를 모두 입력해 주세요.`,
};

/**
 * 검증 메시지 키에 대응하는 msg 디스크립터. 키가 아닌 문자열(서버가 그대로 돌려준
 * better-auth 에러 문구 등)이면 undefined 를 돌려주므로 호출부가 원문을 그대로 쓰면 된다.
 */
export function authMessageDescriptor(key: string | undefined): MessageDescriptor | undefined {
  if (!key) return undefined;
  return MESSAGES[key as AuthMessageKey];
}

// better-auth(@better-auth/core)가 인증 실패 응답으로 돌려주는 에러 코드 → 화면 문구.
// better-auth 는 영어 message(예: "Invalid email or password")를 항상 채워 보내므로, 화면에는
// message 대신 안정적인 code 로 번역해야 한국어(및 en 카탈로그)로 뜬다. 매핑에 없는 코드는
// undefined 를 돌려주므로 호출부가 자기 화면에 맞는 일반 문구로 폴백한다(영어 message 는 쓰지 않는다).
const AUTH_SERVER_ERROR_MESSAGES: Record<string, MessageDescriptor> = {
  // 자격 실패는 계정 존재 여부를 흘리지 않도록 세 코드를 같은 문구로 뭉갠다
  // (better-auth 기본 동작도 대부분 INVALID_EMAIL_OR_PASSWORD 하나로 응답한다).
  INVALID_EMAIL_OR_PASSWORD: msg`이메일 또는 비밀번호가 올바르지 않습니다.`,
  INVALID_PASSWORD: msg`이메일 또는 비밀번호가 올바르지 않습니다.`,
  USER_NOT_FOUND: msg`이메일 또는 비밀번호가 올바르지 않습니다.`,
  USER_ALREADY_EXISTS: msg`이미 가입된 이메일이에요.`,
  INVITATION_EXPIRED: msg`만료된 초대예요. 초대한 사람에게 다시 요청하세요.`,
  INVITATION_NOT_FOUND: msg`유효하지 않은 초대예요.`,
  // EMAIL_NOT_VERIFIED 는 호출부(로그인 페이지)가 화면 전환으로 분기하므로 toast 문구는 두지 않는다.
};

/**
 * better-auth 서버 에러 코드에 대응하는 msg 디스크립터. 매핑에 없는 코드(또는 code 없음)면
 * undefined 를 돌려주므로, 호출부는 영어 message 대신 자기 화면에 맞는 일반 폴백 문구를 쓴다.
 */
export function authServerErrorDescriptor(code: string | undefined): MessageDescriptor | undefined {
  if (!code) return undefined;
  return AUTH_SERVER_ERROR_MESSAGES[code];
}
